import { useEffect, useMemo, useState } from "react";
import { Button } from "../../../components/button/button";
import { AdminHeader } from "../../../components/adminHeader/adminHeader";
import { useLocation, useParams } from "react-router";
import { getTime } from "../../../utils/getStrTime";
import { InputBlock } from "./InputBlock";
import { useAnimatedNavigate } from "../../../hooks/useAnimatedNavigate";
import { Spring } from "../../../components/spring/spring";
import AdminAPI from "../../../api/AdminAPI";
import TableAPI from "../../../api/TableAPI";
import qs from "qs";

import styles from "./addWorkerShiftPage.module.css";

import { Text } from "../../../components/text/text";

export const WorkerShiftAddPage = () => {
    const { userId, cafeId } = useParams();

    const location = useLocation();
    const { search } = location;
    const searchParams = qs.parse(search.replace("?", ""));

    const [date, setDate] = useState(searchParams.date ? new Date(searchParams.date) : new Date());
    const dates = useMemo(() => {
        if (!searchParams.dates) return [];
        const rawDates = Array.isArray(searchParams.dates) ? searchParams.dates : [searchParams.dates];
        return rawDates.map(d => new Date(+d));
    }, [searchParams.dates]);
    const isBulk = dates.length > 0;

    const [startTime, setStartTime] = useState(new Date(1970, 0, 1, 8, 0));

    const [endTime, setEndTime] = useState(new Date(1970, 0, 1, 16, 0));

    const [employees, setEmployees] = useState([]);

    const [employee, setEmployee] = useState(null);
    const [selectedEmployees, setSelectedEmployees] = useState([]);

    useEffect(() => {
        if (!userId) return;
        const found = employees.find(el => String(el.userId) === String(userId));
        setEmployee(found || null);
        if (found && isBulk) {
            setSelectedEmployees(prev => {
                if (prev.findIndex(el => el.userId === found.userId) !== -1) return prev;
                return prev.length > 0 ? prev : [found];
            });
        }
    }, [userId, employees, isBulk]);

    useEffect(() => {
        if (cafeId === null) return;
        AdminAPI
            .fetchCafeEmployees(cafeId)
            .then(res => setEmployees(res))
            .catch(console.error);
    }, [cafeId]);

    const navigate = useAnimatedNavigate()
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const onSave = async () => {
        if (!cafeId) return;

        setLoading(true);
        try {
            const isSuperadmin = location.pathname.startsWith("/superadmin");
            const baseCafePath = isSuperadmin ? `/superadmin/cafe/${cafeId}` : `/admin/${cafeId}/cafe`;

            if (isBulk) {
                const employeeIds = selectedEmployees.map(el => el.userId);
                await TableAPI.bulkCreateShifts(cafeId, employeeIds, dates, getTime(startTime), getTime(endTime));

                const firstDate = dates[0] || new Date();
                const goToEmployee = !!userId && selectedEmployees.length === 1;
                if (goToEmployee) {
                    navigate(`${baseCafePath}/employees/${userId}/table?initialYear=${firstDate.getFullYear()}&initialMonth=${firstDate.getMonth()}`);
                } else {
                    navigate(`${baseCafePath}/table?initialYear=${firstDate.getFullYear()}&initialMonth=${firstDate.getMonth()}`);
                }
            } else {
                await TableAPI.createShift(cafeId, employee.userId, date, getTime(startTime), getTime(endTime));
                if (userId) {
                    const isSuperadmin = location.pathname.startsWith("/superadmin");
                    const baseCafePath = isSuperadmin ? `/superadmin/cafe/${cafeId}` : `/admin/${cafeId}/cafe`;
                    navigate(`${baseCafePath}/employees/${userId}/table?initialYear=${date.getFullYear()}&initialMonth=${date.getMonth()}`);
                } else {
                    const isSuperadmin = location.pathname.startsWith("/superadmin");
                    const baseCafePath = isSuperadmin ? `/superadmin/cafe/${cafeId}` : `/admin/${cafeId}/cafe`;
                    navigate(`${baseCafePath}/table?initialYear=${date.getFullYear()}&initialMonth=${date.getMonth()}`);
                }
            }
        } catch (err) {
            console.error(err);
            setError(true);
        }
        setLoading(false);
    }

    useEffect(() => {
        setError(false)
    }, [date, dates, startTime, endTime, employee, selectedEmployees, isBulk]);

    return (
        <>
            <AdminHeader title="Добавить сотрудника" />

            <Text as="div" variant="body" className="page_description" >
                Выберите сотрудника и укажите время начала и конца смены.
            </Text>

            <InputBlock
                type="user"
                title={isBulk ? "Укажите сотрудников, которых хотите поставить на смены" : "Укажите сотрудника, которого хотите поставить на смену"}
                multiple={isBulk}
                value={isBulk ? selectedEmployees : employee}
                values={employees}
                setValue={isBulk ? setSelectedEmployees : setEmployee}
            />

            <Text as="div" variant="body" className={styles.middle_text} size={12} weight={400}>
                Укажите дату, время начала и конца смены
            </Text>

            {!isBulk && (
                <InputBlock
                    type="date"
                    title="Дата"
                    value={date.toISOString().split("T")[0]}
                    setValue={(val) => {
                        try {
                            const newDate = new Date(val);
                            newDate.toISOString();
                            setDate(newDate);
                        } catch {
                            setDate(prev => prev);
                        }
                    }}
                />
            )}

            <div className={styles.double_inputs}>
                <InputBlock
                    type="time"
                    title="Начало"
                    value={getTime(startTime)}
                    setValue={val => {
                        const parsed = val.split(":");
                        setStartTime(new Date(1970, 0, 1, +parsed[0], +parsed[1]));
                    }}
                />

                <InputBlock
                    type="time"
                    title="Конец"
                    value={getTime(endTime)}
                    setValue={val => {
                        const parsed = val.split(":");
                        setEndTime(new Date(1970, 0, 1, +parsed[0], +parsed[1]));
                    }}
                />
            </div>

            <Spring />

            <Button
                text="Сохранить"
                onClick={onSave}
                disabled={loading || (isBulk ? selectedEmployees.length === 0 : !employee)}
                type={error ? "destructive" : "primary"}
            />
        </>
    )
}
